import React, { useState, createContext, useEffect, useContext } from "react";
import { ethers } from "ethers";
import {
  CONTRACT_ADDRESS,
  ABI_20,
  ICE_ADDRESS,
  RPC_PROVIDER,
  META_SERVER,
} from "../constants/web3";
import { BASE_DG_URL } from "@/common/Fetch";
import { GlobalContext } from "@/store";
import { DGMarketplaceInstance } from "dg-marketplace-sdk";
import Fetch from "@/common/Fetch";
import { fixIpfsImage, isUrl } from "@/store/web3.utils";
import useWebSocket, { ReadyState } from "react-use-websocket";

const defaultValues = {
  contract: null,
  signer: null,
  contractAddress: CONTRACT_ADDRESS,
  hasMetamask: false,
  DGMarketplaceInstance: null,
  balanceIce: 0,
  iceApproved: false,
  allowedIceAmount: 0,
  userCollections: [],
  userCollectionsAddresses: [],
  userCollectionsWallet: [],
  userCompleteTokenList: [],
  loadingSellMoralis: false,
  modalBody: "",
  modalTitle: "",
  createNotification: () => {},
  closeNotification: () => {},
  openModal: false,
};

DGMarketplaceInstance.init({
  backend_url: BASE_DG_URL,
  gasServer_url: META_SERVER,
  polygonRpcProvider_url: RPC_PROVIDER,
});

const normalizeTokenInformation = (token) => {
  const metadata = token.metadata;
  const resourceId = metadata?.id || token.id.tokenId;
  const metadataName = metadata.name;
  const image = [];
  image.push(fixIpfsImage(metadata.image));

  return {
    id: token.id.tokenId,
    resourceId,
    address: token.contract.address,
    name: token.title,
    images: image,
    metadataName,
    animation_url: metadata.animation_url,
  };
};

export const Web3Context = createContext(defaultValues);

const Web3Provider = ({ children }) => {
  const [contract, setContract] = useState(null);
  const [signer, setSigner] = useState(null);
  const [hasMetamask, setHasMetamask] = useState(false);
  const [balanceIce, setBalanceIce] = useState(0);
  const [iceApproved, setIceApproved] = useState(false);
  const [allowedIceAmount, setAllowedIceAmount] = useState(0);

  const [userCollectionsAddresses, setUserCollectionsAddresses] = useState([]);
  const [userCollections, setUserCollections] = useState([]);

  /*Sell screen */
  const [loadingSellMoralis, setLoadingSellMoralis] = useState(true);
  const [userCollectionsWallet, setUserCollectionsWallet] = useState([]);
  const [tokensInCollection, setTokensInCollection] = useState([]);

  /*Notifications: move to a separate context */
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalBody, setModalBody] = useState("");

  const createNotification = (title, body) => {
    setModalTitle(title);
    setModalBody(body);
    setOpenModal(true);
  };

  const closeNotification = () => {
    setOpenModal(false);
  };

  const [state, dispatch] = useContext(GlobalContext);

  const SOCKET_URL = `wss://backend-dg-ice.ddns.net/ws?address=${state.userAddress}`;
  const { lastMessage, readyState } = useWebSocket(SOCKET_URL);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      try {
        const messageJson = JSON.parse(lastMessage?.data);
        if (messageJson.type === "buy") {
          switch (messageJson.status) {
            case "pending":
              waitForTransactionToComplete(messageJson.transactionHash);

              break;
            case "failed":
              createNotification(
                "Error",
                `Sorry there was an error with your transaction`
              );
              break;
            default:
              break;
          }
        }
      } catch (error) {
        console.log("error ws ", error); //TODO: Handle better
      }
    }
  }, [lastMessage, readyState]);

  useEffect(() => {
    if (!state.userAddress) return;

    const { signer, contract } = DGMarketplaceInstance.getContract(
      state.userAddress
    );

    setHasMetamask(true);
    setSigner(signer);
    setContract(contract);
    getUserCollections(state.userAddress);

    getCollectionsOwnedByUser(state.userAddress);

    DGMarketplaceInstance.initProvider(window.ethereum, "metamask");
  }, [state.userAddress]);

  useEffect(() => {
    checkApproval(state.userAddress);
  }, [state.userAddress, signer]);

  const getCollectionsOwnedByUser = async (wallet) => {
    try {
      const collections = await Fetch.GET_ALCHEMY_COLLECTIONS(wallet);
      const collectionsWithType = collections?.contracts.filter(
        (_) => _.tokenType === "ERC721" && _.media[0].raw.length > 0
      );
      setUserCollectionsWallet(collectionsWithType || []);
      setLoadingSellMoralis(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllUserNftFromWallet = async (wallet, contractAddress) => {
    let hasMore = true;
    let cursor = undefined;
    const resultArray = [];

    do {
      const json = await Fetch.GET_ALCHEMY_NFT(
        wallet,
        100,
        cursor,
        contractAddress
      );

      const parseTokenResult = json.ownedNfts.filter(
        (_) =>
          _.contractMetadata.tokenType === "ERC721" && _.metadata && _.title
      );

      resultArray.push(...parseTokenResult);

      cursor = json.pageKey;

      if (!cursor) {
        hasMore = false;
      }
    } while (hasMore === true);

    for (let x = 0; x < resultArray.length; x++) {
      const tokenList = normalizeTokenInformation(resultArray[x]);
      resultArray[x] = tokenList;
    }

    const newTokenList = [...tokensInCollection];
    newTokenList[contractAddress] = resultArray;

    setTokensInCollection(newTokenList);

    return resultArray;
  };

  const waitForTransactionToComplete = async (transactionHash) => {
    try {
      createNotification(
        "Please wait",
        `We are verifying your payment. Transaction hash: ${transactionHash}`
      );

      const transactionStatus =
        await DGMarketplaceInstance.getTransactionStatus(transactionHash);

      if (transactionStatus?.status) {
        createNotification("Success", "Item bought successfully!");
      } else {
        createNotification(
          "Error",
          `Sorry, there was an error processing your request. Please try again later. Transaction hash: ${transactionHash}`
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const createUserCollection = async (address, name, type) => {
    try {
      const status = await Fetch.MINTER_NEW_COLLECTION(
        state.userAddress,
        address,
        name,
        type
      ); //TODO: add real type

      if (status?.data) {
        //await Fetch.MINTER_SYNC_MORALIS(address);

        setUserCollectionsAddresses([...userCollectionsAddresses, status.data]);
      }
    } catch (error) {
      console.log("error posting collection", error);
    }
  };

  const getUserCollections = async (wallet) => {
    if (!wallet) return;

    try {
      const collections = await Fetch.MINTER_GET_COLLECTIONS(wallet);

      if (collections?.data?.length) {
        setUserCollectionsAddresses(collections.data);
      }
    } catch (error) {
      console.log("Error returning minted collections", error);
    }

    //setUserCollectionsAddresses(newData[0]?.collections || []);

    //find in moralis every collection that has the wallet address
  };

  const checkApproval = async (userAddress) => {
    if (userAddress && signer) {
      const iceContract = new ethers.Contract(ICE_ADDRESS, ABI_20, signer);

      const iceAllowance = await iceContract.allowance(
        userAddress,
        CONTRACT_ADDRESS
      );

      const balance = await iceContract.balanceOf(userAddress);
      const balanceFormatted = ethers.utils.formatEther(balance.toString());
      const balanceNumber = Math.round(balanceFormatted * 1e4) / 1e4;

      //alert("your balance is " + balanceNumber);
      setBalanceIce(balanceNumber);

      const allowedIce = ethers.utils.formatEther(iceAllowance.toString());

      const allowedIceAmount = Math.round(allowedIce * 1e4) / 1e4;

      setAllowedIceAmount(allowedIceAmount);
      if (allowedIceAmount > 999) {
        // show toast
        setIceApproved(true);
      } else {
        dispatch({
          type: "show_toastMessage",
          data: `Allowed ICE amount: ${allowedIceAmount}. You need at least 999`,
        });
      }
    }
  };

  return (
    <Web3Context.Provider
      value={{
        contract,
        signer,
        contractAddress: CONTRACT_ADDRESS,
        hasMetamask,
        balanceIce,
        iceApproved,
        allowedIceAmount,
        DGMarketplace: DGMarketplaceInstance,
        userCollections,
        createUserCollection,
        userCollectionsAddresses,
        loadingSellMoralis,
        userCollectionsWallet,
        tokensInCollection,
        setTokensInCollection,
        getAllUserNftFromWallet,
        modalBody,
        modalTitle,
        createNotification,
        closeNotification,
        openModal,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};

export default Web3Provider;
